/**
 * NotchedPanel — HUD-style card with clipped corners + hairline brackets.
 * Ported from primitives.jsx:124-274. Cycle-109 v2 Sprint-1 (S1.4).
 *
 * Subtle, elegant — NOT sci-fi. Background is a clip-path layer; the crisp
 * outline is a separate SVG stroke (clip-path edges don't render borders).
 */
import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

export type NotchedCorner = 'tl' | 'tr' | 'bl' | 'br';

export interface NotchedPanelProps {
  corners?: NotchedCorner[];
  notch?: number;
  ticks?: boolean;
  inner?: boolean;
  bg?: string;
  border?: string;
  accent?: string;
  style?: CSSProperties;
  innerStyle?: CSSProperties;
  children?: ReactNode;
}

function buildClipPath(w: number, h: number, corners: NotchedCorner[], n: number): string {
  const tl = corners.includes('tl');
  const tr = corners.includes('tr');
  const br = corners.includes('br');
  const bl = corners.includes('bl');
  const pts: Array<[number, number]> = [];
  if (tl) {
    pts.push([0, n]);
    pts.push([n, 0]);
  } else {
    pts.push([0, 0]);
  }
  if (tr) {
    pts.push([w - n, 0]);
    pts.push([w, n]);
  } else {
    pts.push([w, 0]);
  }
  if (br) {
    pts.push([w, h - n]);
    pts.push([w - n, h]);
  } else {
    pts.push([w, h]);
  }
  if (bl) {
    pts.push([n, h]);
    pts.push([0, h - n]);
  } else {
    pts.push([0, h]);
  }
  return 'polygon(' + pts.map(([x, y]) => `${x}px ${y}px`).join(', ') + ')';
}

function buildStrokePath(w: number, h: number, corners: NotchedCorner[], n: number): string {
  if (!w || !h) return '';
  const tl = corners.includes('tl');
  const tr = corners.includes('tr');
  const br = corners.includes('br');
  const bl = corners.includes('bl');
  const pts: Array<[number, number]> = [];
  if (tl) {
    pts.push([0, n]);
    pts.push([n, 0]);
  } else {
    pts.push([0, 0]);
  }
  if (tr) {
    pts.push([w - n, 0]);
    pts.push([w, n]);
  } else {
    pts.push([w, 0]);
  }
  if (br) {
    pts.push([w, h - n]);
    pts.push([w - n, h]);
  } else {
    pts.push([w, h]);
  }
  if (bl) {
    pts.push([n, h]);
    pts.push([0, h - n]);
  } else {
    pts.push([0, h]);
  }
  return 'M ' + pts.map((p) => p.join(' ')).join(' L ') + ' Z';
}

function buildInnerPath(w: number, h: number, corners: NotchedCorner[], notch: number, innerInset: number): string {
  if (!w || !h) return '';
  const tl = corners.includes('tl');
  const tr = corners.includes('tr');
  const br = corners.includes('br');
  const bl = corners.includes('bl');
  const i = innerInset;
  const n = Math.max(2, notch - i);
  const pts: Array<[number, number]> = [];
  if (tl) {
    pts.push([i, i + n]);
    pts.push([i + n, i]);
  } else {
    pts.push([i, i]);
  }
  if (tr) {
    pts.push([w - i - n, i]);
    pts.push([w - i, i + n]);
  } else {
    pts.push([w - i, i]);
  }
  if (br) {
    pts.push([w - i, h - i - n]);
    pts.push([w - i - n, h - i]);
  } else {
    pts.push([w - i, h - i]);
  }
  if (bl) {
    pts.push([i + n, h - i]);
    pts.push([i, h - i - n]);
  } else {
    pts.push([i, h - i]);
  }
  return 'M ' + pts.map((p) => p.join(' ')).join(' L ') + ' Z';
}

export function NotchedPanel({
  corners = ['tr'],
  notch = 14,
  ticks = true,
  inner = true,
  bg = 'var(--bg-raised)',
  border = 'var(--border)',
  accent,
  style,
  children,
  innerStyle,
}: NotchedPanelProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });

  useLayoutEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const ro = new ResizeObserver(() => {
      setSize({ w: el.offsetWidth, h: el.offsetHeight });
    });
    ro.observe(el);
    setSize({ w: el.offsetWidth, h: el.offsetHeight });
    return () => ro.disconnect();
  }, []);

  const w = size.w;
  const h = size.h;
  const strokePath = buildStrokePath(w, h, corners, notch);
  const innerInset = 4;
  const innerPath = buildInnerPath(w, h, corners, notch, innerInset);
  const clip = w && h ? buildClipPath(w, h, corners, notch) : undefined;

  return (
    <div ref={ref} style={{ position: 'relative', ...style }}>
      {/* Background layer — clipped */}
      <div
        aria-hidden="true"
        style={{ position: 'absolute', inset: 0, background: bg, clipPath: clip }}
      />
      {/* Accent stripe on top (optional) — follows the notch */}
      {accent && w > 0 && (
        <svg
          aria-hidden="true"
          width={w}
          height={h}
          style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
        >
          <path
            d={(() => {
              const tl = corners.includes('tl');
              const tr = corners.includes('tr');
              const n = notch;
              const pts: Array<[number, number]> = [];
              if (tl) {
                pts.push([0, n]);
                pts.push([n, 0]);
              } else {
                pts.push([0, 0]);
              }
              if (tr) {
                pts.push([w - n, 0]);
                pts.push([w, n]);
              } else {
                pts.push([w, 0]);
              }
              return 'M ' + pts.map((p) => p.join(' ')).join(' L ');
            })()}
            stroke={accent}
            strokeWidth="2"
            fill="none"
          />
        </svg>
      )}
      {/* Outer hairline + optional inner + tick brackets */}
      {w > 0 && (
        <svg
          aria-hidden="true"
          width={w}
          height={h}
          style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
        >
          <path d={strokePath} stroke={border} strokeWidth="1" fill="none" shapeRendering="crispEdges" />
          {inner && (
            <path
              d={innerPath}
              stroke={border}
              strokeWidth="1"
              fill="none"
              opacity="0.55"
              shapeRendering="crispEdges"
            />
          )}
          {ticks && (
            <g strokeWidth="1" opacity="0.35" shapeRendering="crispEdges">
              {!corners.includes('tl') && (
                <g key="tl">
                  <line x1={3} y1={3} x2={3 + 8} y2={3} stroke={border} />
                  <line x1={3} y1={3} x2={3} y2={3 + 8} stroke={border} />
                </g>
              )}
              {!corners.includes('tr') && (
                <g key="tr">
                  <line x1={w - 3} y1={3} x2={w - 3 - 8} y2={3} stroke={border} />
                  <line x1={w - 3} y1={3} x2={w - 3} y2={3 + 8} stroke={border} />
                </g>
              )}
              {!corners.includes('br') && (
                <g key="br">
                  <line x1={w - 3} y1={h - 3} x2={w - 3 - 8} y2={h - 3} stroke={border} />
                  <line x1={w - 3} y1={h - 3} x2={w - 3} y2={h - 3 - 8} stroke={border} />
                </g>
              )}
              {!corners.includes('bl') && (
                <g key="bl">
                  <line x1={3} y1={h - 3} x2={3 + 8} y2={h - 3} stroke={border} />
                  <line x1={3} y1={h - 3} x2={3} y2={h - 3 - 8} stroke={border} />
                </g>
              )}
            </g>
          )}
        </svg>
      )}
      {/* Content */}
      <div style={{ position: 'relative', height: '100%', ...innerStyle }}>{children}</div>
    </div>
  );
}

export default NotchedPanel;
